import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtAUD, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DateRangePicker from "@/components/DateRangePicker";
import { Pill, STAGE_STYLES } from "@/components/badges";
import { Download, Users, PieChart, Filter, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DATE_FIELDS = [
  { id: "close", label: "Expected close date" },
  { id: "created", label: "Created date" },
  { id: "closed", label: "Actual close (Won/Lost)" },
];

const STAGE_ORDER = ["Need Assessment", "Formal Presentation", "Decision Pending", "Closed Won", "Closed Lost"];

export default function PipelineReport() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [dateField, setDateField] = useState("close");
  const [groupBy, setGroupBy] = useState("combined"); // 'combined' | 'owner'
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // ids
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Load users list (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active))).catch(() => setUsers([]));
  }, [isAdmin]);

  const params = useMemo(() => {
    const p = { date_field: dateField, group_by: groupBy };
    if (dateRange.from) p.date_from = dateRange.from;
    if (dateRange.to) p.date_to = dateRange.to;
    if (isAdmin && selectedUsers.length) p.user_ids = selectedUsers.join(",");
    return p;
  }, [dateField, groupBy, dateRange.from, dateRange.to, selectedUsers, isAdmin]);

  const load = async () => {
    setLoading(true);
    setData(null); // clear stale shape before refetch to avoid render mismatch on view-mode change
    try {
      const { data } = await api.get("/reports/pipeline", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [JSON.stringify(params)]);

  const downloadCSV = async () => {
    try {
      const qs = new URLSearchParams();
      qs.set("date_field", dateField);
      if (dateRange.from) qs.set("date_from", dateRange.from);
      if (dateRange.to) qs.set("date_to", dateRange.to);
      if (isAdmin && selectedUsers.length) qs.set("user_ids", selectedUsers.join(","));
      const token = localStorage.getItem("eyb_token");
      const res = await fetch(`${BACKEND_URL}/api/reports/pipeline.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `pipeline_${dateField}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Pipeline CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const toggleUser = (uid) => {
    setSelectedUsers((cur) => (cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]));
  };

  const selectedLabel = selectedUsers.length === 0
    ? "All team members"
    : selectedUsers.length === 1
      ? users.find((u) => u.id === selectedUsers[0])?.name || "1 selected"
      : `${selectedUsers.length} selected`;

  return (
    <div className="p-8" data-testid="pipeline-report-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyb-label">Insights</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Pipeline Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? "Run a snapshot for any selection of team members, combined or separately."
              : "Snapshot of your active pipeline. Filter by date to forecast or audit closings."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="pipeline-export-csv"
            onClick={downloadCSV}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-md hover:bg-slate-50"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="eyb-card p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="pipeline-filters">
        <div>
          <div className="eyb-label mb-1">Date field</div>
          <select
            data-testid="pipeline-date-field"
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
          >
            {DATE_FIELDS.map((f) => (<option key={f.id} value={f.id}>{f.label}</option>))}
          </select>
        </div>
        <div>
          <div className="eyb-label mb-1">Period</div>
          <DateRangePicker value={dateRange} onChange={setDateRange} defaultPreset="all" />
        </div>

        {isAdmin && (
          <>
            <div className="relative">
              <div className="eyb-label mb-1">Team members</div>
              <button
                data-testid="pipeline-user-trigger"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="inline-flex items-center justify-between gap-2 min-w-[200px] px-3 py-2 border border-slate-200 rounded-md text-sm bg-white hover:bg-slate-50"
              >
                <span className="flex items-center gap-2">
                  <Users className="w-3.5 h-3.5 text-slate-500" />
                  <span>{selectedLabel}</span>
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {userMenuOpen && (
                <div
                  className="absolute z-30 mt-1 w-72 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg py-1"
                  data-testid="pipeline-user-menu"
                >
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100">
                    <button
                      type="button"
                      data-testid="pipeline-user-select-all"
                      onClick={() => setSelectedUsers([])}
                      className="text-[11px] font-semibold text-[#187199] hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserMenuOpen(false)}
                      className="p-0.5 hover:bg-slate-100 rounded"
                    >
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                  {users.map((u) => (
                    <label
                      key={u.id}
                      data-testid={`pipeline-user-opt-${u.id}`}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span className="flex-1 text-slate-700">{u.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{u.role.replace("_", " ")}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="eyb-label mb-1">View mode</div>
              <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="pipeline-mode-toggle">
                <button
                  data-testid="pipeline-mode-combined"
                  onClick={() => setGroupBy("combined")}
                  className={`px-3 py-2 text-xs font-semibold ${groupBy === "combined" ? "bg-[#29ABE2] text-white" : "bg-white text-slate-600"}`}
                >
                  Combined
                </button>
                <button
                  data-testid="pipeline-mode-owner"
                  onClick={() => setGroupBy("owner")}
                  className={`px-3 py-2 text-xs font-semibold ${groupBy === "owner" ? "bg-[#29ABE2] text-white" : "bg-white text-slate-600"}`}
                >
                  Per member
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Body */}
      {loading || !data ? (
        <div className="text-sm text-slate-400" data-testid="pipeline-loading">Loading…</div>
      ) : isAdmin && groupBy === "owner" && data.mode === "owner" ? (
        <PerOwnerView data={data} />
      ) : data.mode === "combined" && data.kpis ? (
        <CombinedView data={data} />
      ) : (
        <div className="text-sm text-slate-400" data-testid="pipeline-loading">Loading…</div>
      )}
    </div>
  );
}

function KpiTiles({ kpis }) {
  if (!kpis) return null;
  const tiles = [
    { label: "Open pipeline", value: fmtAUD(kpis.open_value), sub: `${kpis.open_count} open opps`, accent: "from-[#29ABE2] to-[#1B4F8B]" },
    { label: "Closed Won", value: fmtAUD(kpis.won_value), sub: `${kpis.won_count} deal${kpis.won_count === 1 ? "" : "s"}`, accent: "from-emerald-400 to-emerald-600" },
    { label: "Closed Lost", value: fmtAUD(kpis.lost_value), sub: `${kpis.lost_count} deal${kpis.lost_count === 1 ? "" : "s"}`, accent: "from-rose-400 to-rose-600" },
    { label: "Win rate", value: `${kpis.win_rate}%`, sub: "Won / (Won + Lost)", accent: "from-violet-400 to-violet-600" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pipeline-kpis">
      {tiles.map((t, i) => (
        <div key={i} className="eyb-card p-4" data-testid={`pipeline-kpi-${i}`}>
          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${t.accent} mb-3`} />
          <div className="eyb-label">{t.label}</div>
          <div className="font-heading text-lg font-bold text-slate-900 mt-0.5">{t.value}</div>
          <div className="text-[11px] text-slate-500 mt-1">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

function StageFunnel({ funnel }) {
  const maxValue = Math.max(1, ...funnel.map((s) => s.value));
  return (
    <div className="eyb-card p-5 mb-6" data-testid="pipeline-funnel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-slate-900">Stage breakdown</h3>
        <span className="text-[11px] text-slate-400">{funnel.reduce((a, b) => a + b.count, 0)} opps</span>
      </div>
      <div className="space-y-2.5">
        {funnel.map((s) => {
          const pct = (s.value / maxValue) * 100;
          return (
            <div key={s.stage} className="flex items-center gap-3" data-testid={`funnel-row-${s.stage.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="w-28 shrink-0">
                <Pill className={STAGE_STYLES[s.stage]}>{s.stage}</Pill>
              </div>
              <div className="flex-1 h-7 rounded-md bg-slate-100 overflow-hidden relative">
                <div
                  className={`h-full ${s.stage === "Closed Won" ? "bg-emerald-500" : s.stage === "Closed Lost" ? "bg-rose-400" : "bg-gradient-to-r from-[#29ABE2] to-[#1B4F8B]"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-32 shrink-0 text-right">
                <div className="text-sm font-semibold text-slate-900">{fmtAUD(s.value)}</div>
                <div className="text-[10px] text-slate-500">{s.count} opps</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OppTable({ rows, showOwner = true }) {
  if (!rows.length) {
    return (
      <div className="eyb-card p-12 text-center" data-testid="pipeline-empty">
        <PieChart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No opportunities match your filters.</p>
      </div>
    );
  }
  return (
    <div className="eyb-card overflow-hidden" data-testid="pipeline-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
            <th className="px-4 py-3 font-semibold">Opportunity</th>
            <th className="px-4 py-3 font-semibold">Stage</th>
            <th className="px-4 py-3 font-semibold text-right">Value</th>
            <th className="px-4 py-3 font-semibold">Expected close</th>
            {showOwner && <th className="px-4 py-3 font-semibold">Owner</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`pipeline-row-${o.id}`}>
              <td className="px-4 py-3">
                <Link to={`/opportunities/${o.id}`} className="font-semibold text-slate-900 hover:text-[#187199]">{o.name}</Link>
                <div className="text-[11px] text-slate-500">{o.business_name}</div>
              </td>
              <td className="px-4 py-3"><Pill className={STAGE_STYLES[o.stage]}>{o.stage}</Pill></td>
              <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtAUD(o.estimated_value)}</td>
              <td className="px-4 py-3 text-xs text-slate-600">{o.expected_close_date ? fmtDate(o.expected_close_date) : "—"}</td>
              {showOwner && <td className="px-4 py-3 text-slate-700">{o.owner_name}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CombinedView({ data }) {
  if (!data?.kpis) return null;
  return (
    <>
      <KpiTiles kpis={data.kpis} />
      <StageFunnel funnel={data.funnel || []} />
      <OppTable rows={data.rows || []} />
    </>
  );
}

function PerOwnerView({ data }) {
  if (!data.groups?.length) {
    return <div className="text-sm text-slate-500" data-testid="pipeline-empty-groups">Select at least one team member.</div>;
  }
  return (
    <div className="space-y-8" data-testid="pipeline-per-owner">
      {data.groups.map((g) => (
        <section key={g.user_id} className="space-y-4" data-testid={`pipeline-owner-${g.user_id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="eyb-label">Team member</div>
              <h2 className="font-heading text-xl font-bold text-slate-900">{g.user_name}</h2>
              <div className="text-[11px] text-slate-500">{g.user_email}</div>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span><span className="font-semibold text-slate-900">{fmtAUD(g.kpis.open_value)}</span> open</span>
              <span><span className="font-semibold text-emerald-700">{g.kpis.win_rate}%</span> win rate</span>
            </div>
          </div>
          <KpiTiles kpis={g.kpis} />
          <StageFunnel funnel={g.funnel} />
          <OppTable rows={g.rows} showOwner={false} />
        </section>
      ))}
    </div>
  );
}
